import { useState, useEffect, useRef, useMemo } from "react";
import Logo from "./Logo";
import SidebarNotifications from "./SidebarNotifications";
import SidebarUser from "./SidebarUser";
import { STRING } from "../constants/string";
import { Outlet, useLocation, Link, useNavigate, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { signoutRequest } from "../redux/action/signinAction";
import Confetti from "react-confetti";
import moment from "moment";
import Utils from "../utility/utility";
import { CELEBRATE_EVENT } from "../utility/celebrate";
import { hasAuthToken } from "../service/apiError";
import { FiChevronRight, FiSearch, FiSidebar } from "react-icons/fi";
import { AnimatePresence, motion } from "motion/react";
import { EASE_OUT_QUINT, PILL_SPRING, SPRING } from "./SidebarMenu";
import CommandPalette, { SHORTCUT_LABEL } from "./CommandPalette";
import { canAccess, isAdmin } from "../navigation/access";
import NewProjectBanner from "./NewProjectBanner";
import BirthdayStrip from "./BirthdayStrip";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "./Breadcrumb";
import { breadcrumbTrail } from "../navigation/breadcrumbs";
import { FiHome } from "react-icons/fi";

/* ============================================================================
   App shell — INSET SIDEBAR layout.

     <div .app-shell>            the canvas (--app-bg)
       <aside .app-sidebar>      brand · nav · footer      260px / 56px rail
       <main .app-inset>         rounded panel (--app-surface)
         greeting · <Outlet/>   (the alert host is mounted app-wide in App.js)

   The panel is a flex sibling of the sidebar, not an offset block, so
   collapsing the rail reflows the content with no margin arithmetic. That is
   also why router.js's `transtion` prop is left alone: it existed to swap
   .content / .contents for the margin the old sidebar needed, and flexbox
   makes it unnecessary. It stays inert rather than being re-plumbed through
   all 54 routes.

   This replaces the horizontal two-row bar (Header + .app-menubar). The bar's
   six artificial groups ("People", "Work", "Tracking"...) existed only so
   fifteen items would fit on one line; a sidebar has vertical room, so the
   real features are back at top level under non-clickable section labels.

   The file is still named Sidebar.js because router.js imports it as the
   layout element for every authenticated route — and now it is one again.

   ROLE GATES live in navigation/access.js, one table keyed by route. The
   sidebar, the command palette (CommandPalette.js, Cmd/Ctrl+K or the search
   row under the brand) and the route guard in front of <Outlet/> all read
   it, so a page a role cannot see in the menu cannot be reached by URL
   either. It is client-side only — the API does not check role.
   ========================================================================== */

const RULEBOOK_URL =
  "https://docs.google.com/document/d/1LJw2CE6_t_WXf5kd76NeaAxH9dQiu91MvYo_uolHpYk/edit?usp=sharing";

// Global chrome preference, not filter state — deliberately NOT registered in
// the route-scoped localStorage cleanup below.
const COLLAPSE_KEY = "sidebarCollapsed";

/* --- motion ---------------------------------------------------------------
   An inline group springs its height open and its rows follow in a short
   stagger; the rail flyout does the same from a slight scale. Variant names
   propagate, so a row only declares its own closed/open look.
   ------------------------------------------------------------------------ */
const MotionLink = motion.create(Link);

const SUB_VARIANTS = {
  closed: {
    height: 0,
    opacity: 0,
    transition: { ...SPRING, opacity: { duration: 0.12 } },
  },
  open: {
    height: "auto",
    opacity: 1,
    transition: {
      ...SPRING,
      opacity: { duration: 0.15 },
      staggerChildren: 0.025,
      delayChildren: 0.04,
    },
  },
};

// the inline row list sits one level below the height wrapper, so it carries
// the stagger itself — orchestration only reaches direct variant children
const LIST_VARIANTS = {
  closed: {},
  open: { transition: { staggerChildren: 0.025, delayChildren: 0.04 } },
};

const FLYOUT_VARIANTS = {
  closed: { opacity: 0, x: -6, scale: 0.97, transition: { duration: 0.12 } },
  open: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: { ...SPRING, staggerChildren: 0.02, delayChildren: 0.03 },
  },
};

const ROW_VARIANTS = {
  closed: { opacity: 0, x: -6, transition: { duration: 0.1 } },
  open: { opacity: 1, x: 0, transition: { duration: 0.2, ease: EASE_OUT_QUINT } },
};

/* --------------------------------------------------------------------------
   The rows of one group — inline under its parent or inside the rail flyout.

   One pill per group (layoutId) sits behind the hovered row, or the active
   row when nothing is hovered, and springs between rows instead of each row
   repainting its own background. Hover state lives here, not in Sidebar, so
   pointer movement re-renders a dozen rows rather than the whole shell.
   -------------------------------------------------------------------------- */
const SubNav = ({
  item,
  path,
  folderData,
  isFolderOpen,
  onToggleFolders,
  onNavigate,
  title,
  ...containerProps
}) => {
  const [hovered, setHovered] = useState(null);
  const activeId = item.children.some((c) => c.to && c.to === path)
    ? path
    : null;
  const target = hovered ?? activeId;

  const track = (id) => ({
    onMouseEnter: () => setHovered(id),
    onFocus: () => setHovered(id),
  });

  const pill = (id) =>
    target === id ? (
      <motion.span
        layoutId={"sb-pill-" + item.key}
        className={"sb-pill" + (id === activeId ? " is-active" : "")}
        transition={PILL_SPRING}
        aria-hidden="true"
      />
    ) : null;

  return (
    <motion.div
      {...containerProps}
      onMouseLeave={() => setHovered(null)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setHovered(null);
      }}
    >
      {title ? <div className="sb-flyout-title">{title}</div> : null}

      {item.children.map((child) =>
        child.to ? (
          <MotionLink
            key={child.label + child.to}
            to={child.to}
            variants={ROW_VARIANTS}
            className={"sb-sublink" + (path === child.to ? " is-active" : "")}
            onClick={onNavigate}
            {...track(child.to)}
          >
            {pill(child.to)}
            <span className="sb-sublink-text">{child.label}</span>
          </MotionLink>
        ) : (
          <motion.span
            key={child.label}
            variants={ROW_VARIANTS}
            className="sb-sublink is-disabled"
          >
            <span className="sb-sublink-text">{child.label}</span>
          </motion.span>
        )
      )}

      {/* dynamic gallery folders, as before */}
      {item.folders && folderData?.length > 0 ? (
        <>
          <motion.button
            type="button"
            variants={ROW_VARIANTS}
            className={
              "sb-sublink sb-sublink-toggle" + (isFolderOpen ? " is-open" : "")
            }
            aria-expanded={isFolderOpen}
            onClick={onToggleFolders}
            {...track("folders")}
          >
            {pill("folders")}
            <span className="sb-sublink-text">Folders</span>
            <i className="fa fa-angle-down sb-sublink-caret" aria-hidden="true"></i>
          </motion.button>
          <AnimatePresence initial={false}>
            {isFolderOpen ? (
              <motion.div
                key="folders"
                className="sb-sub-folders"
                variants={SUB_VARIANTS}
                initial="closed"
                animate="open"
                exit="closed"
              >
                {folderData.map((folder, index) => (
                  <MotionLink
                    key={folder?.id ?? index}
                    to={"gallery"}
                    variants={ROW_VARIANTS}
                    className="sb-sublink sb-sublink-nested"
                    onClick={onNavigate}
                    {...track("folder:" + index)}
                  >
                    {pill("folder:" + index)}
                    <span className="sb-sublink-text">
                      {folder?.name ?? folder}
                    </span>
                  </MotionLink>
                ))}
              </motion.div>
            ) : null}
          </AnimatePresence>
        </>
      ) : null}
    </motion.div>
  );
};

const Sidebar = (props) => {
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSE_KEY) === "1"
  );
  const [openKeys, setOpenKeys] = useState([]);
  const [flyout, setFlyout] = useState(null);
  // The flyout is position:fixed and anchored to the button it opened from.
  // It has to escape .sb-nav, which is a scroll container (overflow-y:auto)
  // and would otherwise clip it away entirely.
  const [flyoutPos, setFlyoutPos] = useState({ top: 0, left: 0 });
  const [isFolderOpen, setIsFolderOpen] = useState(false);
  const [folderData, setFolderData] = useState([]);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // greeting + birthday, moved out of the old Header
  const [showWelcomeMsg, setShowWelcomeMsg] = useState("");
  const [birthday, setBirthday] = useState({ status: false });
  const windowSize = useRef([window.innerWidth, window.innerHeight]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const signdata = useSelector((state) => state.signinReducers);
  const location = useLocation();
  const sidebarRef = useRef(null);

  /* A session is the token, not the object around it. A persisted `data` with a
     blank access_token used to render the whole shell and every screen fired
     requests the API rejected with 401 "Wrong authorization header format", so
     it is signed out here and sent back to login instead. */
  useEffect(() => {
    if (!signdata) return;
    if (hasAuthToken(signdata?.data?.access_token)) return;

    if (signdata?.data) {
      Utils.toast("error", STRING.SESSION_EXPIRED);
      dispatch(signoutRequest());
    }
    navigate("login");
  }, [navigate, dispatch, signdata]);

  /* Route guard. A URL outside this user's role renders <Navigate/> in place of
     the page, so the page never mounts (and never fires its requests) before
     the bounce. Rules: navigation/access.js. */
  const allowed = canAccess(location.pathname, signdata?.data);
  useEffect(() => {
    if (!allowed && hasAuthToken(signdata?.data?.access_token)) {
      Utils.toast("error", STRING.NO_ACCESS);
    }
    // once per URL, not again when the session object is re-created
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // --- unchanged from the original implementation -------------------------
  useEffect(() => {
    if (
      location.pathname !== "/manage-user" &&
      !location.pathname.startsWith("/edit-user/")
    ) {
      localStorage.removeItem("manageUser");
      localStorage.removeItem("currentPage");
      localStorage.removeItem("manageUserRoles");
      localStorage.removeItem("manageUserStatus");
    }
    if (
      location.pathname !== "/manage-leave" &&
      !location.pathname.startsWith("/edit-leave/")
    ) {
      localStorage.removeItem("leaveDateFilter");
      localStorage.removeItem("leavePagination");
    }
    if (
      location.pathname !== "/manage-clickup" &&
      !location.pathname.startsWith("/add-clickup/")
    ) {
      localStorage.removeItem("filterEmail");
    }
  }, [location.pathname]);

  useEffect(() => {
    const storedData = localStorage.getItem("folderData");
    if (storedData) {
      setFolderData(JSON.parse(storedData));
    }
  }, []);
  // ------------------------------------------------------------------------

  /* --- greeting / birthday, lifted from Header ---------------------------
     Header ran this twice in two effects that computed the same thing; it runs
     once here. The behaviour it kept is the part that matters: the welcome
     line shows once per user per day (tracked in _id/_date), a birthday swaps
     the copy and fires confetti for 10s, and a session with no `dob` is
     signed out.
     ---------------------------------------------------------------------- */
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!signdata?.data) return;

    if (!signdata?.data?.hasOwnProperty("dob")) {
      dispatch(signoutRequest());
      return;
    }

    const id = localStorage.getItem("_id");
    const date = localStorage.getItem("_date");
    const today = Utils.momentDate(STRING.SLASH_DATE_FORMAT, new Date());

    if (
      !id ||
      !date ||
      parseInt(id) !== parseInt(signdata?.data?.id) ||
      date.toString() !== today.toString()
    ) {
      setShowWelcomeMsg("Welcome Back");
      localStorage.setItem("_id", signdata?.data?.id);
      localStorage.setItem("_date", today);
    }

    const currentDate = moment().format("yyyy-MM-DD").split("-");
    const birthdate = signdata?.data?.dob?.split("-");

    if (
      birthdate?.length === 3 &&
      currentDate.length === 3 &&
      birthdate[1] === currentDate[1] &&
      birthdate[2] === currentDate[2]
    ) {
      setShowWelcomeMsg("Happy Birthday!");
      setBirthday({ status: true });
      setTimeout(() => setBirthday({ status: false }), 10000);
    }
  }, []);

  /**
   * A birthday anywhere in the team plays the animation - the dashboards know
   * whose birthday it is today and announce it.
   */
  useEffect(() => {
    const onCelebrate = () => {
      setBirthday({ status: true });
      setTimeout(() => setBirthday({ status: false }), 10000);
    };
    window.addEventListener(CELEBRATE_EVENT, onCelebrate);
    return () => window.removeEventListener(CELEBRATE_EVENT, onCelebrate);
  }, []);

  // close the rail flyout when clicking outside the sidebar
  useEffect(() => {
    const onDocClick = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setFlyout(null);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    setFlyout(null);
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
    if (collapsed) setFlyout(null);
  }, [collapsed]);

  const user = signdata?.data;
  const role = user?.role;

  const fa = (name) => <i className={"fa " + name} aria-hidden="true"></i>;

  /* --------------------------------------------------------------------
     Sections are labels, not click targets. Every route and every label from
     the bar version survives; nothing was added or dropped.

     Who sees what is not decided here: each row lists every page it can
     hold and navigation/access.js drops the ones outside this user's role.
     A group survives with at least one routed child left, a section with at
     least one item. Rulebook is the one external link and keeps its own gate.
     -------------------------------------------------------------------- */
  const SECTIONS = useMemo(
    () => [
      {
        title: "Platform",
        items: [
          {
            key: STRING.DASHBOARD,
            label: "Dashboard",
            icon: fa("fa-tachometer"),
            to: "home",
          },
          {
            key: "timelog",
            label: "Timelog",
            icon: fa("fa-clock-o"),
            children: [
              { label: "My Timelog", to: "timelog" },
              { label: "Saturday Working", to: "saturday-working" },
            ],
          },
          {
            key: "leave",
            label: "Leave",
            icon: fa("fa-calendar"),
            children: [
              { label: "Add Leave", to: "add-leave" },
              { label: "Manage Leave", to: "manage-leave" },
              { label: "Leave Balance", to: "leave-balance" },
              { label: "My Leaves", to: "my-leave" },
            ],
          },
          {
            key: STRING.SUPPORT,
            label: "Support",
            icon: fa("fa-life-ring"),
            children: [
              { label: "Add Support", to: "add-support" },
              { label: "View Support", to: "view-support" },
            ],
          },
          {
            key: "rulebook",
            label: "Rulebook",
            icon: fa("fa-book"),
            show: role !== 0,
            external: RULEBOOK_URL,
          },
        ],
      },
      {
        title: "People",
        items: [
          {
            key: "users",
            label: "Manage Users",
            icon: fa("fa-users"),
            children: [
              { label: "Add User", to: "add-user" },
              { label: "Manage Users", to: "manage-user" },
              { label: "Deleted Users" },
            ],
          },
          {
            key: "payslips",
            label: "Pay Slips",
            icon: fa("fa-money"),
            children: [
              { label: "Add Payslips", to: "add-payslip" },
              { label: "Manage Payslips", to: "manage-payslip" },
            ],
          },
          {
            key: "clockin",
            label: "Clock IN",
            icon: fa("fa-hourglass-half"),
            to: "clock-in",
          },
        ],
      },
      {
        title: "Delivery",
        items: [
          {
            key: "projects",
            label: "Manage Projects",
            icon: fa("fa-folder-open"),
            children: [
              { label: "Add Projects", to: "add-project" },
              { label: "Manage Projects", to: "manage-project" },
            ],
          },
          {
            key: "myprojects",
            label: "My Projects",
            icon: fa("fa-folder-open"),
            to: "my-projects",
          },
          {
            key: "tasks",
            label: "Task List",
            icon: fa("fa-tasks"),
            children: [{ label: "Manage Tasks", to: "manage-task" }],
          },
          {
            key: "todo",
            label: "To do",
            icon: fa("fa-check-square-o"),
            children: [{ label: "Manage To do", to: "manage-todo" }],
          },
          {
            key: "tracker",
            label: "Manage Tracker",
            icon: fa("fa-line-chart"),
            children: [
              { label: "Add Tracker", to: "add-tracker" },
              { label: "Manage Tracker", to: "manage-tracker" },
            ],
          },
          {
            key: "clickup",
            label: "Clickup Tracker",
            icon: fa("fa-columns"),
            children: [
              { label: "Add Tracker", to: "add-clickup" },
              { label: "Manage Tracker", to: "manage-clickup" },
            ],
          },
        ],
      },
      {
        title: "Workplace",
        items: [
          {
            key: "anouncements",
            label: "Announcements",
            icon: fa("fa-bullhorn"),
            children: [
              { label: "Add Announcements", to: "add-anouncements" },
              { label: "Listing", to: "annoucement-listing" },
            ],
          },
          {
            key: "birthdaywishes",
            label: "Birthday Wishes",
            icon: fa("fa-birthday-cake"),
            to: "birthday-wishes",
          },
          {
            key: "gallery",
            label: "Gallery",
            icon: fa("fa-picture-o"),
            folders: true,
            children: [
              { label: "Create Folder", to: "createFolder" },
              { label: "Listing", to: "gallery" },
            ],
          },
          {
            key: "inventory",
            label: "Inventory",
            icon: fa("fa-cube"),
            children: [
              { label: "Add Inventory", to: "add-inventory" },
              { label: "Listing", to: "inventory-listing" },
            ],
          },
        ],
      },
    ]
      .map((sec) => ({
        ...sec,
        items: sec.items
          .map((it) =>
            it.children
              ? {
                  ...it,
                  // a route-less row (Deleted Users) is a placeholder, not a page
                  children: it.children.filter(
                    (c) => !c.to || canAccess(c.to, user)
                  ),
                }
              : it
          )
          .filter((it) => {
            if (it.external) return it.show;
            if (it.children) return it.children.some((c) => c.to);
            return canAccess(it.to, user);
          }),
      }))
      .filter((sec) => sec.items.length > 0),
    [user]
  );

  /* --- active route ------------------------------------------------------
     Derived from the URL rather than tracked in state, so a deep link or a
     back button highlights the right item instead of only a click doing it.
     --------------------------------------------------------------------- */
  const path = location.pathname.replace(/^\//, "");

  const isItemActive = (item) => {
    if (item.to) return path === item.to;
    if (item.children) {
      return item.children.some((c) => c.to && path === c.to);
    }
    return false;
  };

  // auto-open the group that owns the current route
  useEffect(() => {
    const owner = SECTIONS.flatMap((s) => s.items).find(
      (it) => it.children && it.children.some((c) => c.to && path === c.to)
    );
    if (owner) {
      setOpenKeys((keys) =>
        keys.includes(owner.key) ? keys : [...keys, owner.key]
      );
    }
  }, [path]);

  const toggleGroup = (item, event) => {
    if (collapsed) {
      if (flyout === item.key) {
        setFlyout(null);
        return;
      }
      const r = event.currentTarget.getBoundingClientRect();
      // keep a tall panel on screen when the row that opened it sits low
      setFlyoutPos({
        top: Math.max(8, Math.min(r.top, window.innerHeight - 240)),
        left: r.right + 6,
      });
      setFlyout(item.key);
      return;
    }
    setOpenKeys((keys) =>
      keys.includes(item.key)
        ? keys.filter((k) => k !== item.key)
        : [...keys, item.key]
    );
  };

  const subNavProps = (item) => ({
    item,
    path,
    folderData,
    isFolderOpen,
    onToggleFolders: () => setIsFolderOpen((open) => !open),
    onNavigate: () => setFlyout(null),
  });

  /* --- breadcrumbs -------------------------------------------------------
     Read off SECTIONS too (navigation/breadcrumbs.js), so a crumb only ever
     links to a page this user's sidebar offers. Empty on the dashboard.
     --------------------------------------------------------------------- */
  const trail = useMemo(
    () => breadcrumbTrail(location.pathname, SECTIONS),
    [location.pathname, SECTIONS]
  );

  /* --- command palette ---------------------------------------------------
     Built from SECTIONS, so it offers exactly the pages this user's sidebar
     does — same role gates, nothing extra. "Deleted Users" has no route and
     the gallery folders all point at `gallery`, so neither is listed.
     --------------------------------------------------------------------- */
  const paletteItems = useMemo(
    () =>
      SECTIONS.flatMap((sec) =>
        sec.items.flatMap((item) => {
          if (item.external) {
            return [
              {
                id: item.key,
                label: item.label,
                group: sec.title,
                parent: "Opens in a new tab",
                icon: item.icon,
                onSelect: () => window.open(item.external, "_blank", "noreferrer"),
              },
            ];
          }
          const leaves = item.children
            ? item.children.filter((c) => c.to)
            : [{ label: item.label, to: item.to }];
          return leaves.map((leaf) => ({
            id: leaf.to,
            label: leaf.label,
            group: sec.title,
            parent: item.children ? item.label : undefined,
            keywords: [leaf.to.replace(/-/g, " ")],
            icon: item.icon,
            badge: path === leaf.to ? "Current" : null,
            onSelect: () => navigate("/" + leaf.to),
          }));
        })
      ),
    [SECTIONS, path, navigate]
  );

  return (
    <div className={"app-shell" + (collapsed ? " is-collapsed" : "")}>
      {birthday?.status ? (
        <Confetti
          width={windowSize.current[0]}
          height={windowSize.current[1]}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            pointerEvents: "none",
            zIndex: 2350,
          }}
        />
      ) : (
        ""
      )}

      <aside className="app-sidebar d-print-none" ref={sidebarRef}>
        <div className="sb-brand">
          <Link to="home" className="sb-brand-link" aria-label="Rapidsofts">
            <Logo className="sb-brand-logo" style={{ objectFit: "contain" }} />
          </Link>
          <button
            type="button"
            className="sb-collapse"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            onClick={() => setCollapsed((c) => !c)}
          >
            <FiSidebar aria-hidden="true" />
          </button>
        </div>

        <button
          type="button"
          className="sb-search"
          aria-label="Search pages"
          aria-keyshortcuts={SHORTCUT_LABEL === "⌘K" ? "Meta+K" : "Control+K"}
          aria-haspopup="dialog"
          aria-expanded={paletteOpen}
          title={collapsed ? `Search pages (${SHORTCUT_LABEL})` : undefined}
          onClick={() => setPaletteOpen(true)}
        >
          <FiSearch className="sb-search-icon" aria-hidden="true" />
          <span className="sb-search-label">Search pages…</span>
          <kbd className="sb-search-kbd">{SHORTCUT_LABEL}</kbd>
        </button>

        <motion.nav
          className="sb-nav"
          layoutScroll
          onScroll={() => setFlyout(null)}
        >
          {SECTIONS.map((sec) => (
            <div className="sb-group" key={sec.title}>
              <div className="sb-group-label">{sec.title}</div>

              {sec.items.map((item) => {
                const active = isItemActive(item);

                // external link (Rulebook)
                if (item.external) {
                  return (
                    <button
                      key={item.key}
                      type="button"
                      className="sb-link"
                      title={collapsed ? item.label : undefined}
                      onClick={() =>
                        window.open(item.external, "_blank", "noreferrer")
                      }
                    >
                      <span className="sb-link-icon">{item.icon}</span>
                      <span className="sb-link-label">{item.label}</span>
                    </button>
                  );
                }

                // leaf item (Dashboard, Clock IN)
                if (!item.children) {
                  return (
                    <Link
                      key={item.key}
                      to={item.to}
                      className={"sb-link" + (active ? " is-active" : "")}
                      title={collapsed ? item.label : undefined}
                    >
                      <span className="sb-link-icon">{item.icon}</span>
                      <span className="sb-link-label">{item.label}</span>
                    </Link>
                  );
                }

                const isOpen = openKeys.includes(item.key);
                const isFlyout = flyout === item.key;

                return (
                  <div
                    className={
                      "sb-item" +
                      (isOpen && !collapsed ? " is-open" : "") +
                      (isFlyout ? " has-flyout" : "")
                    }
                    key={item.key}
                  >
                    <button
                      type="button"
                      className={"sb-link" + (active ? " is-active" : "")}
                      aria-expanded={collapsed ? isFlyout : isOpen}
                      title={collapsed ? item.label : undefined}
                      onClick={(e) => toggleGroup(item, e)}
                    >
                      <span className="sb-link-icon">{item.icon}</span>
                      <span className="sb-link-label">{item.label}</span>
                      <FiChevronRight
                        className="sb-link-chev"
                        aria-hidden="true"
                      />
                    </button>

                    {/* expanded: inline accordion. rail: flyout panel. */}
                    <AnimatePresence initial={false}>
                      {collapsed && isFlyout ? (
                        <SubNav
                          key="flyout"
                          {...subNavProps(item)}
                          title={item.label}
                          className="sb-flyout"
                          style={{ top: flyoutPos.top, left: flyoutPos.left }}
                          variants={FLYOUT_VARIANTS}
                          initial="closed"
                          animate="open"
                          exit="closed"
                        />
                      ) : null}
                      {!collapsed && isOpen ? (
                        <motion.div
                          key="sub"
                          className="sb-sub-wrap"
                          variants={SUB_VARIANTS}
                          initial="closed"
                          animate="open"
                          exit="closed"
                        >
                          <SubNav
                            {...subNavProps(item)}
                            className="sb-sub"
                            variants={LIST_VARIANTS}
                          />
                        </motion.div>
                      ) : null}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          ))}
        </motion.nav>

        <div className="sb-foot">
          <SidebarNotifications collapsed={collapsed} />
          <SidebarUser collapsed={collapsed} />
        </div>
      </aside>

      <CommandPalette
        items={paletteItems}
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
      />

      <main className="app-inset">
        {/* on the signed-in user's birthday, above every page all day */}
        <BirthdayStrip />

        {allowed && trail.length ? (
          <Breadcrumb className="app-breadcrumb">
            <BreadcrumbList>
              {trail.map((crumb, index) => {
                const last = index === trail.length - 1;
                const label = (
                  <>
                    {crumb.key === "home" ? <FiHome aria-hidden="true" /> : null}
                    {crumb.label}
                  </>
                );
                return (
                  <BreadcrumbItem key={crumb.key}>
                    {index > 0 ? <BreadcrumbSeparator /> : null}
                    {last ? (
                      <BreadcrumbPage>{label}</BreadcrumbPage>
                    ) : crumb.to ? (
                      <BreadcrumbLink
                        render={(props) => <Link {...props} to={"/" + crumb.to} />}
                      >
                        {label}
                      </BreadcrumbLink>
                    ) : (
                      <span className="bc-text">{label}</span>
                    )}
                  </BreadcrumbItem>
                );
              })}
            </BreadcrumbList>
          </Breadcrumb>
        ) : null}

        {/* the dashboard greets in its own headline (WelcomeHeadline.js);
            a birthday still gets this line there */}
        {showWelcomeMsg &&
        !((path === "" || path === "home") && showWelcomeMsg === "Welcome Back") ? (
          <div className="app-greeting">
            <h4 className="mb-0 fw-bold">
              {showWelcomeMsg},
              <span className="site-color">
                {` ${signdata?.data?.first_name ?? ""} ${
                  signdata?.data?.last_name ?? ""
                }`}
              </span>
            </h4>
          </div>
        ) : null}

        {/* admins assign projects; only employees are told about them */}
        {!isAdmin(user) ? <NewProjectBanner /> : null}

        {allowed ? <Outlet /> : <Navigate to="/home" replace />}
      </main>
    </div>
  );
};

export default Sidebar;
